import React, { useState, useEffect } from 'react';
import './Contact.css';
import { MapPin, Phone, Envelope, Clock, PaperPlaneTilt } from 'phosphor-react';
import { API_BASE_URL } from '../config/api';
import { trackContactForm } from '../utils/analytics';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Preencher formulário com dados do utilizador logado
  useEffect(() => {
    const username = localStorage.getItem('username');
    const email = localStorage.getItem('email');
    const phone = localStorage.getItem('phone');
    const token = localStorage.getItem('token');
    
    if (username && email && token) {
      setFormData(prev => ({
        ...prev,
        name: username,
        email: email,
        phone: phone || ''
      }));
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Verificar se a resposta é JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        // Se não for JSON, é provavelmente HTML de erro
        throw new Error('Servidor não disponível. Verifique se o backend está rodando.');
      }

      if (response.ok && data.status === 'ok') {
        setSubmitStatus({
          type: 'success',
          message: data.message
        });
        
        // Track envio de formulário de contato
        trackContactForm(formData.service);
        
        // Limpar formulário
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Erro ao enviar mensagem. Tente novamente.'
        });
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Erro de conexão. Verifique se o servidor está rodando.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-container">
      <div className="contact-header">
        <h1>Contacte-nos</h1>
        <p>Estamos aqui para ajudar com as suas necessidades de segurança</p>
      </div>

      <div className="contact-content">
        <div className="contact-info">
          <div className="info-card">
            <h2>Informações de Contacto</h2>
            
            <div className="contact-item">
              <MapPin size={24} />
              <div>
                <h3>Localização</h3>
                <p>Rua do Mercado nº298<br />Vale de Cambra, Portugal</p>
              </div>
            </div>

            <div className="contact-item">
              <Phone size={24} />
              <div>
                <h3>Telefone</h3>
                <p>+351 256 418 202</p>
              </div>
            </div>

            <div className="contact-item">
              <Envelope size={24} />
              <div>
                <h3>Email</h3>
                <p>mlptelecomunicacoes@gmail.com</p>
              </div>
            </div>

            <div className="contact-item">
              <Clock size={24} />
              <div>
                <h3>Horário de Funcionamento</h3>
                <p>Segunda a Sexta: 9h-12h | 14h-19h<br />Sábado: 9h-12h30<br />Domingo: Fechado</p>
              </div>
            </div>
          </div>

          <div className="services-card">
            <h2>Nossos Serviços</h2>
            <ul>
              <li>Videovigilância (CCTV)</li>
              <li>Sistemas de Alarme</li>
              <li>Videoporteiros</li>
              <li>Instalação e Configuração</li>
              <li>Manutenção e Suporte</li>
              <li>Orçamentos Gratuitos</li>
            </ul>
          </div>
        </div>

        <div className="contact-form">
          <div className="form-card">
            <h2>Entre em contacto connosco</h2>
            <p>Preencha o formulário e entraremos em contacto consigo</p> 
            <form onSubmit={handleSubmit}>
              {submitStatus && (
                <div className={`status-message ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="name">Nome Completo *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Telefone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="service">Serviço de Interesse</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                >
                  <option value="">Selecione um serviço</option>
                  <option value="videovigilancia">Videovigilância</option>
                  <option value="alarmes">Sistemas de Alarme</option>
                  <option value="videoporteiros">Videoporteiros</option>
                  <option value="manutencao">Manutenção</option>
                  <option value="outro">Outro</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Mensagem *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  placeholder="Descreva as suas necessidades ou dúvidas..."
                  required
                ></textarea>
              </div>

              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                <PaperPlaneTilt size={20} />
                {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
